import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";

export const MADLO_DEFAULT = {
  nom_application: "MADLO Constat",
  couleur_primaire: "#C0562B",
  couleur_survol: "#A8481F",
  couleur_fonce: "#1F1E1C",
  couleur_texte_secondaire: "#8C8981",
  couleur_filet: "#E5E3DE",
  police: "Inter",
  logo_file_id: "",
  logo_fond_blanc: false,
  logo_url: "",
  agence: { nom: "", adresse: "" },
};

const hexToRgb = (hex) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255} ${(n >> 8) & 255} ${n & 255}`;
};

export function applyBranding(b, neutral = false) {
  const r = document.documentElement.style;
  const set = (k, hex) => { const v = hexToRgb(hex); if (v) r.setProperty(k, v); };
  set("--brand-primary", b.couleur_primaire);
  set("--brand-hover", b.couleur_survol);
  set("--brand-dark", b.couleur_fonce);
  set("--brand-muted", b.couleur_texte_secondaire);
  set("--brand-line", b.couleur_filet);
  r.setProperty("--brand-font", `'${b.police || "Inter"}'`);
  document.title = b.nom_application || "MADLO Constat";
  const href = b.favicon_url || "/favicon.png";
  let link = document.querySelector("link[rel='icon']");
  if (!link) { link = document.createElement("link"); link.rel = "icon"; document.head.appendChild(link); }
  if (link.getAttribute("href") !== href) { link.setAttribute("href", href); link.removeAttribute("type"); }
  const slug = neutral ? "" : (localStorage.getItem("madlo.agence") || "");
  const q = slug ? `&agence=${encodeURIComponent(slug)}` : "";
  const v = b.favicon_file_id || b.logo_file_id || "madlo";
  const ensure = (rel, url) => {
    let l = document.querySelector(`link[rel='${rel}']`);
    if (!l) { l = document.createElement("link"); l.rel = rel; document.head.appendChild(l); }
    if (l.getAttribute("href") !== url) l.setAttribute("href", url);
  };
  ensure("manifest", `${API}/branding/manifest?v=${v}${q}`);
  ensure("apple-touch-icon", `${API}/branding/icon?size=180&v=${v}${q}`);
  const meta = document.querySelector("meta[name='theme-color']");
  if (meta && b.couleur_primaire) meta.setAttribute("content", b.couleur_primaire);
}

const Ctx = createContext({ branding: MADLO_DEFAULT, refresh: () => {}, applyNeutral: () => {}, exitNeutral: () => {} });

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(MADLO_DEFAULT);
  // Sur les pages publiques (avant connexion) on ne connaît pas la gérance :
  // on force le branding neutre MADLO tant que ce drapeau est actif.
  const neutralRef = useRef(false);
  const refresh = useCallback(async () => {
    if (neutralRef.current) { setBranding(MADLO_DEFAULT); applyBranding(MADLO_DEFAULT, true); return; }
    try {
      const slug = localStorage.getItem("madlo.agence") || undefined;
      const { data } = await api.get("/branding", { params: slug ? { agence: slug } : {} });
      if (neutralRef.current) { setBranding(MADLO_DEFAULT); applyBranding(MADLO_DEFAULT, true); return; }
      if (data.agence?.slug) localStorage.setItem("madlo.agence", data.agence.slug);
      const b = { ...MADLO_DEFAULT, ...data, logo_url: data.logo_url ? `${API}${data.logo_url.replace(/^\/api/, "")}` : "",
        favicon_url: data.favicon_url ? `${API}${data.favicon_url.replace(/^\/api/, "")}` : "" };
      setBranding(b); applyBranding(b);
    } catch { applyBranding(MADLO_DEFAULT); }
  }, []);
  const applyNeutral = useCallback(() => {
    neutralRef.current = true;
    setBranding(MADLO_DEFAULT);
    applyBranding(MADLO_DEFAULT, true);
  }, []);
  const exitNeutral = useCallback(() => { neutralRef.current = false; }, []);
  useEffect(() => { applyBranding(MADLO_DEFAULT); refresh(); }, [refresh]);
  return <Ctx.Provider value={{ branding, refresh, applyNeutral, exitNeutral }}>{children}</Ctx.Provider>;
}

export const useBranding = () => useContext(Ctx);
